// clipboard.writeText rejects with NotAllowedError when Chrome denies
// clipboard-write, even on a click. Quasar's copyToClipboard only falls back
// to execCommand when navigator.clipboard is missing, so that rejection escaped
// click handlers and Vue reported it. Copy synchronously in this turn first:
// writeText rejects later, after the user gesture is gone.
function copyWithExecCommand(text: string): boolean {
  if (typeof document === 'undefined' || !document.body) {
    return false
  }

  const area = document.createElement('textarea')
  area.value = text
  area.setAttribute('readonly', '')
  area.setAttribute('aria-hidden', 'true')
  area.style.contain = 'strict'
  area.style.position = 'fixed'
  area.style.left = '-9999px'
  area.style.top = '0'
  area.style.fontSize = '12pt'

  const selection = document.getSelection()
  const previousRanges: Range[] = []
  if (selection) {
    for (let i = 0; i < selection.rangeCount; i++) {
      previousRanges.push(selection.getRangeAt(i))
    }
  }
  const previouslyFocused = document.activeElement

  document.body.appendChild(area)
  let copied = false
  try {
    area.select()
    copied = document.execCommand('copy')
  } catch {
    copied = false
  } finally {
    area.remove()
    if (selection) {
      selection.removeAllRanges()
      for (const range of previousRanges) {
        selection.addRange(range)
      }
    }
    previouslyFocused?.focus({ preventScroll: true })
  }
  return copied
}

export async function copyText(text: string): Promise<void> {
  if (copyWithExecCommand(text)) {
    return
  }

  const clipboard = typeof navigator === 'undefined' ? undefined : navigator.clipboard
  if (!clipboard?.writeText) {
    throw new Error('Copy failed')
  }

  try {
    await clipboard.writeText(text)
  } catch {
    throw new Error('Copy failed')
  }
}
